import React, { Fragment, useState } from 'react';
import { Help, Icon, Modal, Notice, Seg } from '@/components/kit/ds';
import { useAppSelector } from '@/hooks/hooks';
import { useStrings } from '@/i18n';
import { formatPriceCents } from '@/constants/billing-display';
import { billingStrings } from '@/i18n/dictionaries/billing';
import type { CheckoutPeriod, PlanTerms } from '@/types/billing';
import {
  exitTierLines,
  planHead,
  revenueTierLines,
  thresholdLine,
  trialLine,
  type TierLine,
} from './planTermsView';

/**
 * WEB-3302 юнит «условия» — окно «Тарифы и условия» (`bal-pricing-dlg`) по
 * эталону `design/cabinet-rescin/sections/org.jsx:905-930`: выбор периода
 * (`bal-pr-period` — `bal-period` уже занят плашкой оплаты), сводная таблица
 * `bal-ptable` из трёх колонок (свои платежи, платежи через нас, Enterprise),
 * под ней — что не входит в пробный.
 *
 * Деньги (R4): все числа — из `planTerms` ответа `GET /billing` (WEB-4008),
 * строки собирает `planTermsView.ts`; окно только рисует. `planTerms: null`
 * (организации нет, служба плана не подключена) — пометка «недоступно» без
 * таблицы и без единого числа. Кнопок Enterprise «Давайте обсудим» и смены
 * периода живой подписки нет — у сервера нет ни заявки, ни смены (эталон
 * сам помечает `BAL_PROP.plans`). Окно справочное: закрывается крестиком, Esc
 * и касанием мимо (без `sticky`/`noEsc`); стоит в странице всегда, открывается
 * по `open` (затухание кита при закрытии).
 */

type S = (typeof billingStrings)['en'];

/** Значение ячейки: текст, галочка или строки ступеней. */
type Cell = string | true | readonly TierLine[] | React.ReactElement;

interface Row {
  readonly label: string;
  /** Колонки: свои платежи, платежи через нас, Enterprise. */
  readonly cells: readonly [Cell, Cell, Cell];
}

function groups(terms: PlanTerms, s: S): ReadonlyArray<readonly [string, readonly Row[]]> {
  const same = (v: Cell): [Cell, Cell, Cell] => [v, v, v];
  const trial = trialLine(terms, s);
  const thr = thresholdLine(terms, s);
  return [
    [
      s.prGrpFunnels,
      [
        { label: s.prRowFunnels, cells: same(s.prUnlimited) },
        { label: s.prRowAi, cells: same(s.prForCredits) },
      ],
    ],
    [
      s.prGrpAnalytics,
      [
        { label: s.prRowAnalytics, cells: same(true) },
        { label: s.prRowEvents, cells: same(true) },
      ],
    ],
    [
      s.prGrpApp,
      [
        { label: s.prRowApp, cells: same(true) },
        { label: s.prRowDomains, cells: same(true) },
        { label: s.prRowMembers, cells: same(s.prNoExtra) },
      ],
    ],
    [
      s.prGrpPay,
      [
        { label: s.prRowBuyer, cells: [s.prOnYourSide, s.prViaStripe, s.prDeal] },
        {
          label: s.prRowVolume,
          cells: [
            s.prPerRedirect,
            <>
              {s.prBoth}
              <Help tip={s.prBothTip} />
            </>,
            s.prDeal,
          ],
        },
        {
          label: s.prRowTiers,
          cells: [exitTierLines(terms, s), revenueTierLines(terms, s), '—'],
        },
      ],
    ],
    [
      s.prGrpStart,
      [
        { label: s.prRowStart, cells: [trial, trial, s.prStartEnt] },
        { label: s.prRowCharges, cells: [thr, thr, s.prInvoiceEnt] },
      ],
    ],
  ];
}

function cell(v: Cell): React.ReactNode {
  if (v === true)
    return (
      <span className="ok">
        <Icon name="check" size={16} />
      </span>
    );
  if (Array.isArray(v))
    return (
      <div className="tl">
        {(v as readonly TierLine[]).map((t) => (
          <span key={t.label}>
            {t.label}
            <i>{t.value}</i>
          </span>
        ))}
      </div>
    );
  return v as React.ReactNode;
}

function Terms({ terms }: { readonly terms: PlanTerms }) {
  const s = useStrings(billingStrings);
  const [period, setPeriod] = useState<CheckoutPeriod>('month');
  const money = (cents: number) => formatPriceCents(cents, terms.currency);
  const head = planHead(terms, period, s);
  const sub = (
    <div className="pr">
      <span className="big">
        {head.price}
        <small>{s.perMonthShort}</small>
      </span>
      {head.yearNote && <div className="hint">{head.yearNote}</div>}
    </div>
  );

  return (
    <>
      <div className="bx-per">
        <Seg<CheckoutPeriod>
          size="sm"
          value={period}
          onChange={setPeriod}
          label={s.planPeriodLabel}
          testid="bal-pr-period"
          options={[
            { value: 'month', label: s.planPeriodMonth(money(terms.tariff.monthlyChargeCents)) },
            { value: 'year', label: s.planPeriodYear(money(terms.tariff.annualMonthlyCents)) },
          ]}
        />
      </div>
      <div className="bx-pw">
        <table className="bx-pt bx-ft" data-testid="bal-ptable">
          <thead>
            <tr>
              <th />
              <th>
                <b>{s.prColOwn}</b>
                <span>{s.prColOwnSub}</span>
                {sub}
              </th>
              <th>
                <b>{s.prColUs}</b>
                <span>{s.prColUsSub}</span>
                {sub}
              </th>
              <th className="ent">
                <b>{s.prColEnt}</b>
                <span>{s.prColEntSub}</span>
                <div className="pr">
                  <span className="big">{s.prDeal}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {groups(terms, s).map(([title, rows]) => (
              <Fragment key={title}>
                <tr className="grp">
                  <td colSpan={4}>{title}</td>
                </tr>
                {rows.map((r) => (
                  <tr key={r.label}>
                    <td>{r.label}</td>
                    <td>{cell(r.cells[0])}</td>
                    <td>{cell(r.cells[1])}</td>
                    <td className="ent">{cell(r.cells[2])}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <span className="hint">{s.prIncTrial}</span>
    </>
  );
}

export interface PlanTermsModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export default function PlanTermsModal({ open, onClose }: PlanTermsModalProps) {
  const s = useStrings(billingStrings);
  const terms = useAppSelector((state) => state.billing?.planTerms ?? null);
  return (
    <Modal
      open={open}
      onClose={onClose}
      width={1040}
      testid="bal-pricing-dlg"
      title={
        <>
          {s.prTitle}
          <Help tip={s.prHelp} />
        </>
      }
    >
      {terms ? (
        <Terms terms={terms} />
      ) : (
        <Notice kind="info" testid="bal-pricing-off">
          {s.prOff}
        </Notice>
      )}
    </Modal>
  );
}
